// src/hooks/useUser.js
import { useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { isProfileComplete } from '../utils/profile';
import i18n from '../i18n';

/**
 * Backward-compatible hook combining auth and profile state.
 * Maintains API compatibility while using new context providers.
 * @returns {Object} User state and methods for legacy consumers
 */
const useUser = () => {
  const authState = useAuth();
  const profileState = useCurrentUserProfile();

  // Excludes saving state to prevent UI flicker during writes
  const loading =
    authState.status === 'loading' ||
    (authState.status === 'signedIn' &&
      (profileState.status === 'loading' || !profileState.hasReceivedSnapshot));

  // Apply language preference as side effect when profile loads
  useEffect(() => {
    if (authState.status !== 'signedIn') return;
    if (!profileState.hasReceivedSnapshot) return;
    if (profileState.status !== 'exists') return;

    const preference = profileState.profile?.languagePreference;
    if (typeof preference !== 'string' || preference.length === 0) return;
    if (i18n.language === preference) return;

    void i18n.changeLanguage(preference).catch((err) => {
      console.error('Failed to apply language preference', err);
    });
  }, [authState.status, profileState.hasReceivedSnapshot, profileState.status, profileState.profile?.languagePreference]);

  const error = useMemo(() => {
    if (profileState.status === 'error') {
      return profileState.error;
    }
    return profileState.saveError || null;
  }, [profileState.error, profileState.saveError, profileState.status]);

  /**
   * Check if profile needs completion.
   * Function (not value) for backward compatibility with legacy code.
   */
  const isProfileIncomplete = useCallback(() => {
    if (authState.status !== 'signedIn') return true;
    if (!profileState.hasReceivedSnapshot) return true;
    if (profileState.status === 'missing') return true;
    return !isProfileComplete(profileState.profile);
  }, [authState.status, profileState.hasReceivedSnapshot, profileState.status, profileState.profile]);

  return {
    user: authState.user,
    userId: authState.uid,
    uid: authState.uid,
    profile: profileState.profile,
    loading,
    error,
    saving: profileState.saving,
    saveError: profileState.saveError,
    updateProfile: profileState.updateProfile,
    updateCompleteUserProfile: profileState.updateCompleteUserProfile,
    isLoggedIn: authState.status === 'signedIn',
    isProfileIncomplete,
  };
};

export default useUser;
